import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import { Button } from '../../components/ui/Button';
import DataTable from '../../components/ui/DataTable';
import {
  TechnicalMaintenancePageShell,
  StatCard,
  formatDisplayDate,
  fmtMoney,
} from './technicalMaintenanceUi';
import {
  aggregateByKey,
  daysUntilDate,
  downloadCsv,
} from './technicalMaintenanceHelpers';

const TechnicalMaintenanceReportsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [equipment, setEquipment] = useState([]);
  const [records, setRecords] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [equipmentRes, recordsRes, invoicesRes] = await Promise.all([
          technicalMaintenanceApi.getEquipment(),
          technicalMaintenanceApi.getMaintenanceRecords(),
          technicalMaintenanceApi.getInvoices(),
        ]);
        setEquipment(equipmentRes?.data || equipmentRes || []);
        setRecords(recordsRes?.data || recordsRes || []);
        setInvoices(invoicesRes?.data || invoicesRes || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalMaintenanceCost = useMemo(
    () => records.reduce((sum, row) => sum + (Number(row.cost) || 0), 0),
    [records]
  );

  const totalInvoiceAmount = useMemo(
    () => invoices.reduce((sum, row) => sum + (Number(row.total_amount) || 0), 0),
    [invoices]
  );

  const byDepartment = useMemo(
    () => aggregateByKey(records, (row) => row.equipment_area, (row) => row.cost),
    [records]
  );

  const byVendor = useMemo(
    () => aggregateByKey(
      records.filter((row) => row.vendor),
      (row) => row.vendor,
      (row) => row.cost
    ),
    [records]
  );

  const warrantyExpiring = useMemo(
    () => equipment
      .filter((item) => item.warranty_expiry_date)
      .map((item) => ({ ...item, days_until: daysUntilDate(item.warranty_expiry_date) }))
      .filter((item) => item.days_until != null && item.days_until <= 90)
      .sort((a, b) => a.days_until - b.days_until),
    [equipment]
  );

  const noIntervalEquipment = useMemo(
    () => equipment.filter((item) => item.is_active && !item.maintenance_interval_days),
    [equipment]
  );

  const exportEquipment = () => {
    downloadCsv(
      'teknik-bakim-ekipman.csv',
      ['Kategori', 'Ad', 'Kod', 'Alan', 'Marka', 'Model', 'Seri No', 'Bakım Aralığı (gün)', 'Garanti Bitiş', 'Aktif'],
      equipment.map((row) => [
        row.category_label,
        row.name,
        row.equipment_code,
        row.area,
        row.brand,
        row.model_name,
        row.serial_number,
        row.maintenance_interval_days ?? '',
        row.warranty_expiry_date ?? '',
        row.is_active ? 'Evet' : 'Hayır',
      ])
    );
  };

  const exportRecords = () => {
    downloadCsv(
      'teknik-bakim-kayitlari.csv',
      ['Ekipman', 'Alan', 'Tip', 'Durum', 'Plan', 'Tamamlanma', 'Sonraki Bakım', 'Firma', 'Yapan', 'Parçalar', 'Maliyet', 'Açıklama'],
      records.map((row) => [
        row.equipment_name,
        row.equipment_area,
        row.maintenance_type_label,
        row.status_label,
        row.scheduled_date,
        row.completed_date,
        row.next_maintenance_date,
        row.vendor,
        row.performed_by,
        row.replaced_parts,
        row.cost ?? '',
        row.description,
      ])
    );
  };

  const exportInvoices = () => {
    downloadCsv(
      'teknik-bakim-faturalar.csv',
      ['Fatura No', 'Tarih', 'Tedarikçi', 'Tip', 'Toplam', 'Ödeme', 'Not'],
      invoices.map((row) => [
        row.invoice_no,
        row.invoice_date,
        row.supplier,
        row.invoice_type_label,
        row.total_amount,
        row.payment_status_label,
        row.notes,
      ])
    );
  };

  const deptColumns = useMemo(() => [
    { header: 'Bölüm', accessorKey: 'key' },
    { header: 'Bakım Sayısı', accessorKey: 'count', cell: ({ getValue }) => getValue() },
    { header: 'Toplam Maliyet', accessorKey: 'cost', cell: ({ getValue }) => `${fmtMoney(getValue())} TRY` },
  ], []);

  const vendorColumns = useMemo(() => [
    { header: 'Firma', accessorKey: 'key' },
    { header: 'İşlem', accessorKey: 'count' },
    { header: 'Toplam', accessorKey: 'cost', cell: ({ getValue }) => `${fmtMoney(getValue())} TRY` },
  ], []);

  return (
    <TechnicalMaintenancePageShell
      title="Raporlar & Dışa Aktarım"
      subtitle="Maliyet özetleri, garanti takibi ve Excel/CSV dışa aktarma."
      actions={(
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={exportEquipment}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1 inline" />
            Ekipman CSV
          </Button>
          <Button variant="secondary" onClick={exportRecords}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1 inline" />
            Bakım CSV
          </Button>
          <Button variant="secondary" onClick={exportInvoices}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1 inline" />
            Fatura CSV
          </Button>
        </div>
      )}
    >
      {loading ? (
        <div className="tm-kpi-grid">
          {[1, 2, 3, 4].map((i) => <div key={i} className="tm-skeleton" style={{ height: 88 }} />)}
        </div>
      ) : (
        <>
          <div className="tm-kpi-grid">
            <StatCard label="Toplam Ekipman" value={equipment.length} accent="rgb(37 99 235)" />
            <StatCard label="Bakım Kaydı" value={records.length} accent="rgb(5 150 105)" />
            <StatCard label="Bakım Maliyeti" value={`${fmtMoney(totalMaintenanceCost)} TRY`} accent="rgb(217 119 6)" />
            <StatCard label="Fatura Toplamı" value={`${fmtMoney(totalInvoiceAmount)} TRY`} accent="rgb(124 58 237)" />
          </div>

          <div className="tm-dash-grid">
            <div className="tm-dash-card">
              <h3>Bölüme Göre Bakım</h3>
              <DataTable columns={deptColumns} data={byDepartment.slice(0, 12)} showSearch={false} />
            </div>
            <div className="tm-dash-card">
              <h3>Firmaya Göre Harcama</h3>
              <DataTable columns={vendorColumns} data={byVendor.slice(0, 12)} showSearch={false} />
            </div>
          </div>

          <div className="tm-dash-grid">
            <div className="tm-dash-card">
              <h3>Garanti Bitişi Yaklaşanlar (90 gün)</h3>
              {warrantyExpiring.length === 0 ? (
                <p className="text-sm text-gray-500">Yaklaşan garanti bitişi yok.</p>
              ) : (
                <ul className="space-y-2">
                  {warrantyExpiring.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 hover:border-highlight-400"
                        onClick={() => navigate(`/internal-audit/technical-maintenance/equipment/${item.id}`)}
                      >
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500">
                          {item.area || '—'} · {formatDisplayDate(item.warranty_expiry_date)}
                          {' · '}
                          {item.days_until < 0 ? `${Math.abs(item.days_until)} gün geçmiş` : `${item.days_until} gün kaldı`}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="tm-dash-card">
              <h3>Bakım Aralığı Tanımsız Ekipmanlar</h3>
              <p className="text-xs text-gray-500 mb-3">
                Periyodik bakım planlaması için ekipman kartına gün aralığı girilmesi önerilir.
              </p>
              {noIntervalEquipment.length === 0 ? (
                <p className="text-sm text-gray-500">Tüm aktif ekipmanlarda aralık tanımlı.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {noIntervalEquipment.slice(0, 15).map((item) => (
                    <li key={item.id} className="text-sm text-gray-700 dark:text-gray-300">
                      {item.name}
                      <span className="text-gray-400"> · {item.area || '—'}</span>
                    </li>
                  ))}
                  {noIntervalEquipment.length > 15 && (
                    <li className="text-xs text-gray-500">+{noIntervalEquipment.length - 15} ekipman daha</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </TechnicalMaintenancePageShell>
  );
};

export default TechnicalMaintenanceReportsPage;
